/**
 * ProtectedRoute.tsx
 * Composant pour protéger les routes nécessitant une authentification
 */
import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthContext } from './AuthProvider';
import { isAdmin as checkIsAdmin } from '../../services/RoleService';
import { logger } from '../../utils/ProfessionalLogger';

export interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'admin' | 'user';
}

export function ProtectedRoute({
  children,
  requiredRole,
}: ProtectedRouteProps): React.ReactElement {
  const { user, loading } = useAuthContext();
  const location = useLocation();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [checkingRole, setCheckingRole] = useState(false);

  // Vérifier le rôle admin si requis
  useEffect(() => {
    if (requiredRole === 'admin' && user && !loading) {
      checkAdminRole();
    } else if (!requiredRole) {
      setIsAdmin(null);
    }
  }, [user, loading, requiredRole]);

  const checkAdminRole = async () => {
    if (!user) return;

    try {
      setCheckingRole(true);
      // PR-05 : Utiliser RoleService avec cache au lieu de requête directe
      const adminStatus = await checkIsAdmin(user.id);
      setIsAdmin(adminStatus);
    } catch (error) {
      logger.error('Erreur lors de la vérification du rôle admin:', error);
      setIsAdmin(false);
    } finally {
      setCheckingRole(false);
    }
  };

  // Afficher un loader pendant la vérification
  if (loading || (requiredRole === 'admin' && checkingRole)) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          width: '100vw',
          background: 'var(--color-bg-secondary)',
        }}
      >
        <div
          style={{
            textAlign: 'center',
            color: 'var(--color-text-secondary)',
          }}
        >
          <div
            style={{
              width: '40px',
              height: '40px',
              border: '4px solid var(--color-border)',
              borderTopColor: 'var(--color-primary)',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px',
            }}
          />
          <p>Chargement...</p>
        </div>
      </div>
    );
  }

  // A15 : si non authentifié, REDIRIGER vers /login en mémorisant la route
  // demandée (location.state.from), afin que l'URL reste cohérente et que
  // l'utilisateur revienne à la bonne page après connexion. LoginPage lit déjà
  // `location.state.from.pathname` pour la redirection post-login.
  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // Vérifier le rôle si requis
  if (requiredRole === 'admin') {
    if (isAdmin === false) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
            background: '#f8fafc',
            color: '#1e293b',
          }}
        >
          <div
            style={{
              background: 'white',
              padding: '32px',
              borderRadius: '8px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              textAlign: 'center',
              maxWidth: '400px',
            }}
          >
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔒</div>
            <h2 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '8px' }}>Accès refusé</h2>
            <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '24px' }}>
              Vous n'avez pas les droits administrateur nécessaires pour accéder à cette page.
            </p>
            <button
              onClick={() => (window.location.href = '/editor')}
              style={{
                padding: '10px 20px',
                background: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 500,
              }}
            >
              Retour à l'éditeur
            </button>
          </div>
        </div>
      );
    }
  }

  // Si authentifié, afficher le contenu protégé
  return <>{children}</>;
}
